/**
 * 画布视图控制器：合成图渲染 + 缩放/平移手势 + 检测框叠加 + 工具交互
 *
 * 手势语义（P4 共识 Q3）：双指 = pinch 缩放+平移（锚点在手势中心）；单指按工具分派。
 * 桌面：滚轮缩放（光标锚点）、双击适应、中键平移。
 */
import type { Analysis, Hold } from "../core/types";

export type ToolKind = "pan" | "select" | "lasso" | "erase" | "exclude";

export interface OverlayStyle {
  selected: number | null;
  excluded: ReadonlySet<number>;
  erased: ReadonlySet<number>;
  visible: boolean;
}

interface PointerState {
  id: number;
  x: number;
  y: number;
  startX: number;
  startY: number;
  moved: boolean;
}

export interface CanvasCallbacks {
  onTap(analysisPt: { x: number; y: number }): void;
  onLasso(pts: Array<{ x: number; y: number }>): void;
  onZoomChange(): void;
}

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 40;

export class CanvasView {
  private analysis: Analysis | null = null;
  private composite: ImageData | null = null;
  private showResult = true;
  private lassoPts: Array<{ x: number; y: number }> = [];

  zoom = 1;
  panX = 0;
  panY = 0;
  tool: ToolKind = "select";
  overlayStyle: OverlayStyle = {
    selected: null,
    excluded: new Set(),
    erased: new Set(),
    visible: true,
  };

  private view: HTMLCanvasElement;
  private overlay: HTMLCanvasElement;
  private vctx: CanvasRenderingContext2D;
  private octx: CanvasRenderingContext2D;
  private offA: HTMLCanvasElement;
  private offB: HTMLCanvasElement;
  private pointers = new Map<number, PointerState>();
  private pinchDist = 0;
  private cb: CanvasCallbacks;

  constructor(cb: CanvasCallbacks) {
    this.cb = cb;
    this.view = document.getElementById("view") as HTMLCanvasElement;
    this.overlay = document.getElementById("overlay") as HTMLCanvasElement;
    this.vctx = this.view.getContext("2d")!;
    this.octx = this.overlay.getContext("2d")!;
    this.offA = document.createElement("canvas");
    this.offB = document.createElement("canvas");
    this.bindGestures();
    this.observeResize();
  }

  /**
   * #13：布局变化（进入编辑态时 dock/线路条显隐、桌面侧栏折叠等）不触发
   * window resize，但会改 #stage 的 flex 尺寸 → canvas 属性尺寸与 CSS 尺寸
   * 失步：清屏区域不足留残影带、整幅内容被 CSS 压缩导致指针↔笔迹错位。
   * ResizeObserver 盯画布盒尺寸；draw() 内另有兜底同步（RO 缺失/迟到也正确）。
   */
  private observeResize(): void {
    if (typeof ResizeObserver === "undefined") return;
    new ResizeObserver(() => this.resize()).observe(this.view);
  }

  setAnalysis(a: Analysis): void {
    this.analysis = a;
    this.offA.width = a.aw;
    this.offA.height = a.ah;
    this.offB.width = a.aw;
    this.offB.height = a.ah;
    this.offB.getContext("2d")!.putImageData(new ImageData(new Uint8ClampedArray(a.img), a.aw, a.ah), 0, 0);
    this.composite = null;
    this.fit();
  }

  setComposite(data: ImageData): void {
    this.composite = data;
    this.offA.getContext("2d")!.putImageData(data, 0, 0);
    this.draw();
  }

  setShowResult(v: boolean): void {
    this.showResult = v;
    this.draw();
  }

  fit(): void {
    const a = this.analysis;
    if (!a) return;
    const r = this.view.getBoundingClientRect();
    this.zoom = Math.min(r.width / a.aw, r.height / a.ah);
    this.panX = (r.width - a.aw * this.zoom) / 2;
    this.panY = (r.height - a.ah * this.zoom) / 2;
    this.cb.onZoomChange();
    this.draw();
  }

  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const r = this.view.getBoundingClientRect();
    this.syncSize(dpr, r);
    this.draw();
  }

  /**
   * 属性尺寸对齐 CSS 盒尺寸（#13 根因修复）。赋值即整幅清屏，天然消除
   * 残影带；尺寸未变时不赋值（避免每次 draw 清屏）。返回是否发生了修正。
   */
  private syncSize(dpr: number, r: DOMRect): boolean {
    const w = Math.round(r.width * dpr);
    const h = Math.round(r.height * dpr);
    let changed = false;
    for (const c of [this.view, this.overlay]) {
      if (c.width !== w) {
        c.width = w;
        changed = true;
      }
      if (c.height !== h) {
        c.height = h;
        changed = true;
      }
    }
    return changed;
  }

  /** 屏幕(px) → 分析坐标 */
  toAnalysis(sx: number, sy: number): { x: number; y: number } {
    return { x: (sx - this.panX) / this.zoom, y: (sy - this.panY) / this.zoom };
  }

  /** 分析坐标 → 屏幕(px)，与 draw() 变换互逆（e2e/定位用） */
  toScreen(ax: number, ay: number): { x: number; y: number } {
    return { x: ax * this.zoom + this.panX, y: ay * this.zoom + this.panY };
  }

  draw(): void {
    const dpr = window.devicePixelRatio || 1;
    const r = this.view.getBoundingClientRect();
    // #13 兜底：任何漏网的布局变化在此纠正——属性尺寸过期 = 变换映射错位
    // （套索偏移）+ 清屏区域不足（残影/花屏）。
    this.syncSize(dpr, r);
    const ctx = this.vctx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#1a1d21";
    ctx.fillRect(0, 0, r.width, r.height);
    if (!this.analysis) return;
    ctx.setTransform(dpr * this.zoom, 0, 0, dpr * this.zoom, dpr * this.panX, dpr * this.panY);
    ctx.imageSmoothingEnabled = this.zoom < 3;
    const src = this.showResult && this.composite ? this.offA : this.offB;
    ctx.drawImage(src, 0, 0);
    this.drawOverlay(dpr, r);
  }

  private drawOverlay(dpr: number, r: DOMRect): void {
    const ctx = this.octx;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, r.width, r.height);
    // 套索笔迹是交互反馈（如套索进行中的轨迹），不随「叠加」开关隐藏——
    // 否则关掉检测框后画套索毫无视觉反馈（#13 笔迹贴合验证也依赖它）
    this.drawLassoStroke(dpr);
    const a = this.analysis;
    if (!a || !this.overlayStyle.visible) return;
    const st = this.overlayStyle;
    ctx.setTransform(dpr * this.zoom, 0, 0, dpr * this.zoom, dpr * this.panX, dpr * this.panY);
    const lw = 1.8 / this.zoom;
    for (const h of a.holds) {
      const w = h.bbox[2] - h.bbox[0];
      const hh = h.bbox[3] - h.bbox[1];
      if (w < 2 || hh < 2) continue;
      const inRoute = st.selected !== null && h.cluster === st.selected;
      const showAll = st.selected === null;
      if (!showAll && !inRoute) continue;
      const isEx = st.excluded.has(h.id);
      const isEr = st.erased.has(h.id);
      ctx.lineWidth = lw;
      if (isEx || isEr) {
        ctx.setLineDash([4 / this.zoom, 3 / this.zoom]);
        ctx.strokeStyle = isEr ? "#d93636" : "#e6a23c";
        ctx.strokeRect(h.bbox[0], h.bbox[1], w, hh);
        ctx.setLineDash([]);
        continue;
      }
      if (inRoute) {
        const cl = a.clusters[st.selected!];
        ctx.fillStyle = `rgba(${cl.rgb[0]},${cl.rgb[1]},${cl.rgb[2]},0.14)`;
        ctx.fillRect(h.bbox[0], h.bbox[1], w, hh);
        ctx.strokeStyle = "#ffffff";
        ctx.shadowColor = "rgba(0,0,0,0.65)";
        ctx.shadowBlur = 2 / this.zoom;
        ctx.strokeRect(h.bbox[0], h.bbox[1], w, hh);
        ctx.shadowBlur = 0;
      } else {
        const cl = h.cluster >= 0 && h.cluster < a.clusters.length ? a.clusters[h.cluster] : null;
        if (!cl) continue;
        ctx.strokeStyle = `rgba(${cl.rgb[0]},${cl.rgb[1]},${cl.rgb[2]},0.75)`;
        ctx.strokeRect(h.bbox[0], h.bbox[1], w, hh);
      }
    }
  }

  /** 套索进行中的笔迹（分析坐标存储，zoom/pan 变换下绘制 → 与手指轨迹同基准） */
  private drawLassoStroke(dpr: number): void {
    if (this.lassoPts.length === 0) return;
    const ctx = this.octx;
    ctx.setTransform(
      dpr * this.zoom, 0, 0, dpr * this.zoom,
      dpr * this.panX, dpr * this.panY,
    );
    ctx.strokeStyle = "#ff5a2c";
    ctx.lineWidth = 2 / this.zoom;
    ctx.beginPath();
    this.lassoPts.forEach((p, i) =>
      i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y),
    );
    ctx.stroke();
  }

  /** 命中测试：bbox 含点、取面积最小（桌面 app.js:378-389 同构）。
   *  ux-review P0：触控容差——屏幕 8px / zoom 换算到分析坐标外扩 bbox，
   *  720 档小岩点在手机上仅 10-20 屏幕像素，无容差几乎点不中 */
  hitTest(ax: number, ay: number): Hold | null {
    const a = this.analysis;
    if (!a) return null;
    const tol = 8 / Math.max(this.zoom, 1e-6);
    let best: Hold | null = null;
    for (const h of a.holds) {
      if (
        ax >= h.bbox[0] - tol &&
        ax <= h.bbox[2] + tol &&
        ay >= h.bbox[1] - tol &&
        ay <= h.bbox[3] + tol
      ) {
        if (!best || h.area < best.area) best = h;
      }
    }
    return best;
  }

  private bindGestures(): void {
    const el = this.view;
    el.addEventListener("pointerdown", (e) => {
      el.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        startX: e.clientX,
        startY: e.clientY,
        moved: false,
      });
      if (this.pointers.size === 2) {
        const [p1, p2] = [...this.pointers.values()];
        this.pinchDist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      }
      if (this.tool === "lasso" && this.pointers.size === 1) {
        const r = el.getBoundingClientRect();
        const pt = this.toAnalysis(e.clientX - r.left, e.clientY - r.top);
        this.lassoPts = [pt];
      }
    });

    el.addEventListener("pointermove", (e) => {
      const p = this.pointers.get(e.pointerId);
      if (!p) return;
      const dx = e.clientX - p.x;
      const dy = e.clientY - p.y;
      p.x = e.clientX;
      p.y = e.clientY;
      if (Math.hypot(e.clientX - p.startX, e.clientY - p.startY) > 8) p.moved = true;

      if (this.pointers.size === 2) {
        // pinch：缩放锚点=手势中心，同时平移
        const [p1, p2] = [...this.pointers.values()];
        const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
        if (this.pinchDist > 0 && dist > 0) {
          const r = el.getBoundingClientRect();
          const cx = (p1.x + p2.x) / 2 - r.left;
          const cy = (p1.y + p2.y) / 2 - r.top;
          this.zoomAt(cx, cy, dist / this.pinchDist);
          this.panX += dx / 2;
          this.panY += dy / 2;
        }
        this.pinchDist = dist;
        this.draw();
        return;
      }
      if (this.tool === "pan") {
        this.panX += dx;
        this.panY += dy;
        this.draw();
      } else if (this.tool === "lasso" && this.lassoPts.length > 0) {
        const r = el.getBoundingClientRect();
        this.lassoPts.push(this.toAnalysis(e.clientX - r.left, e.clientY - r.top));
        this.draw();
      }
    });

    const up = (e: PointerEvent) => {
      const p = this.pointers.get(e.pointerId);
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this.pinchDist = 0;
      if (!p) return;
      const r = el.getBoundingClientRect();
      if (this.tool === "lasso") {
        if (this.lassoPts.length >= 3 && p.moved) {
          this.cb.onLasso(this.lassoPts);
        } else {
          // <3 点或未移动 = 点击
          const pt = this.toAnalysis(e.clientX - r.left, e.clientY - r.top);
          this.cb.onTap(pt);
        }
        this.lassoPts = [];
        this.draw();
        return;
      }
      if (!p.moved) {
        const pt = this.toAnalysis(e.clientX - r.left, e.clientY - r.top);
        this.cb.onTap(pt);
      }
    };
    el.addEventListener("pointerup", up);
    el.addEventListener("pointercancel", up);

    // 滚轮缩放（桌面）
    el.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        const r = el.getBoundingClientRect();
        this.zoomAt(
          e.clientX - r.left,
          e.clientY - r.top,
          Math.exp(-e.deltaY / 400),
        );
        this.draw();
        this.cb.onZoomChange();
      },
      { passive: false },
    );
    el.addEventListener("dblclick", (e) => e.preventDefault());
    el.addEventListener("contextmenu", (e) => e.preventDefault());
  }

  private zoomAt(cx: number, cy: number, factor: number): void {
    const nz = Math.min(Math.max(this.zoom * factor, MIN_ZOOM), MAX_ZOOM);
    const k = nz / this.zoom;
    this.panX = cx - (cx - this.panX) * k;
    this.panY = cy - (cy - this.panY) * k;
    this.zoom = nz;
    this.cb.onZoomChange();
  }
}
